import classNames from "classnames";
import useMountEntrance, { IMountEntrance } from "../../../shared/hooks/useMountEntrance";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaH5 from "../typography/H5/H5";
import KuikaSmallMediumText from "../typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../typography/SmallText/SmallText";
import "./kuika-empty-state.scss";
import KuikaText from "../typography/Text/Text";

interface IRawTileStyle extends React.CSSProperties {
  "--kuika-icon-tile-size"?: string;
}

/** A consumer's raw svgWidth/svgHeight, mapped onto the icon tile: the width becomes the tile's
 *  side (radius follows it), a different height stretches it. Unset → the ladder rung decides. */
const getRawTileStyle = (width: number | undefined, height: number | undefined): IRawTileStyle | undefined => {
  if (width === undefined && height === undefined) {
    return undefined;
  }
  const side: number = width ?? height ?? 0;
  const style: IRawTileStyle = { "--kuika-icon-tile-size": `${side}px` };
  if (height !== undefined && height !== side) {
    style.height = `${height}px`;
  }
  return style;
};

const KuikaEmptyState = ({
  svgName,
  icon,
  svgType = "common" as const,
  name,
  customName,
  description,
  onDescriptionClick,
  action,
  footer,
  pastTense = "added",
  hideDescription = false,
  svgWidth,
  svgHeight,
  variant = "default"
}: {
  /** Leave unset for an icon-less state — the tile is dropped and the card keeps title + body. */
  svgName?: string;
  /** A glyph the caller already built, for a host that composes its icon conditionally —
   *  KuikaTable's `emptyIcon`. Mutually exclusive with `svgName`, and takes precedence over it. */
  icon?: React.ReactNode;
  customName?: string;
  svgType?:
    | "common"
    | "datasource-icons"
    | "sidebar-icons"
    | "styling-panel-icons"
    | "action-icons"
    | "arrows"
    | "topbar-icons"
    | "flags"
    | "workflow-icons"
    | "screen-templates"
    | "screen-templates-icons"
    | "agent-icons";
  name?: string;
  description?: string | React.ReactNode;
  /** A call to action below the description — a button that starts what the panel is missing. */
  footer?: React.ReactNode;
  action?: string;
  pastTense?: string;
  onDescriptionClick?: (e) => void;
  hideDescription?: boolean;
  /** Raw tile size, outside the tile ladder (md 48 · page lg 64). The radius follows the width;
   *  a different height stretches the tile. Prefer the ladder — leave both unset. */
  svgWidth?: number;
  svgHeight?: number;
  /** "page": large, flat page-level empty state (64px icon, H5 title, 14px body).
   *  "embedded": the compact card alone, for a surface that already frames it (a dropdown list, a
   *  panel section's body) — no panel ground, rule or gutter of its own around the card.
   *  "table": the same flat text as "page" but with the caller's glyph left untiled — the shape a
   *  table's empty band has always had, inside a `<td>` that owns the band's own floor.
   *  "default": the compact carded panel empty state used everywhere else. */
  variant?: "default" | "page" | "embedded" | "table";
}) => {
  const isPage: boolean = variant === "page";
  const isTable: boolean = variant === "table";
  // Both flat variants read at page scale: H5 title over a 14px body. Only the tile differs.
  const isFlat: boolean = isPage || isTable;
  const title: string = customName || `No ${name} ${pastTense} yet.`;
  const { isEntering, onAnimationEnd }: IMountEntrance = useMountEntrance();
  // The panel variant rises as one card; the page variant staggers icon → title → description →
  // footer, so the entrance ends on whichever of those it renders last.
  const hasFooter: boolean = footer !== undefined && footer !== null;
  const hasIcon: boolean = icon !== undefined || svgName !== undefined;
  const isIconLast: boolean = hasIcon && isPage && hideDescription && !hasFooter;
  const isDescriptionLast: boolean = isPage && !hideDescription && !hasFooter;
  return (
    <div
      className={classNames("kuika-empty-state", {
        "kuika-empty-state--iconless": !hasIcon,
        "kuika-empty-state--page": isPage,
        "kuika-empty-state--table": isTable,
        "kuika-empty-state--embedded": variant === "embedded",
        "kuika-empty-state--entering": isEntering
      })}
      onAnimationEnd={isEntering ? onAnimationEnd : undefined}
    >
      <div className="kuika-empty-state__container" data-entrance-last={!isPage || undefined}>
        {hasIcon && (
          <div
            className="kuika-empty-state__icon"
            style={isFlat ? undefined : getRawTileStyle(svgWidth, svgHeight)}
            data-entrance-last={isIconLast || undefined}
          >
            {icon ?? (svgName !== undefined && <KuikaSvg type={svgType} name={svgName} />)}
          </div>
        )}
        {!hideDescription && (
          <div className="kuika-empty-state__description">
            {isFlat ? <KuikaH5>{title}</KuikaH5> : <KuikaSmallMediumText>{title}</KuikaSmallMediumText>}
            {isFlat ? (
              <KuikaText data-entrance-last={isDescriptionLast || undefined}>{description}</KuikaText>
            ) : (
              <KuikaSmallText>
                {onDescriptionClick && (
                  <KuikaSmallText className="kuika-empty-state__description--link" onClick={onDescriptionClick}>
                    {action || "Create"} a {name?.toLowerCase()}{" "}
                  </KuikaSmallText>
                )}
                {description}
              </KuikaSmallText>
            )}
          </div>
        )}
        {hasFooter && (
          <div className="kuika-empty-state__footer" data-entrance-last={isPage || undefined}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default KuikaEmptyState;
